import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, GlobalSearchResponse, ContactGroup } from '../api/client';

/**
 * Global Search Interface
 * 
 * Search across all contacts with results grouped by contact.
 * Requirements: 8.6
 */

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  async function handleSearch() {
    if (!query.trim()) return;
    
    try {
      setLoading(true);
      setError(null);
      setHasSearched(true);
      
      const searchResults = await api.globalSearch(query.trim());
      setResults(searchResults);
    } catch (err) {
      setError('Search failed. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function highlightQuery(text: string, searchQuery: string): JSX.Element {
    const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
    return (
      <>
        {parts.map((part, i) => 
          part.toLowerCase() === searchQuery.toLowerCase() ? (
            <mark key={i} style={{ background: '#fef08a', padding: '0 2px' }}>{part}</mark>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  }

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>Search All Calls</h2>
      
      {/* Search Box */}
      <div className="card">
        <div className="search-box">
          <input
            type="text"
            className="input"
            placeholder="Search across all contacts..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
          <button 
            className="btn btn-primary"
            onClick={handleSearch}
            disabled={loading || !query.trim()}
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="card" style={{ color: 'var(--error)' }}>
          {error}
        </div>
      )}

      {/* Results */}
      {loading && (
        <div className="loading">
          <div className="spinner" />
          Searching...
        </div>
      )}

      {!loading && hasSearched && results && (
        <div className="search-results">
          {results.totalResults === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🔍</div>
              <p>No results found for "{query}"</p>
              <p style={{ fontSize: 14, marginTop: 8 }}>
                Try different keywords or check your spelling
              </p>
            </div>
          ) : (
            <>
              <p style={{ marginBottom: 16, color: 'var(--text-secondary)' }}>
                Found {results.totalResults} results across {results.groupedResults.length} contacts
              </p>
              
              {results.groupedResults.map((group: ContactGroup) => (
                <div key={group.contactId} className="search-result-group">
                  <div 
                    className="search-result-group-header"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/contact/${group.contactId}`)}
                  >
                    <span style={{ color: 'var(--primary)' }}>
                      {group.contactName}
                    </span>
                    <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>
                      ({group.results.length} matches)
                    </span>
                  </div>
                  
                  {group.results.map((result, i) => (
                    <div 
                      key={i} 
                      className="search-result-item"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/contact/${group.contactId}`)}
                    >
                      <div className="search-result-meta">
                        📞 {formatDate(result.callDate)}
                      </div>
                      <div className="search-result-snippet">
                        {highlightQuery(result.snippet, query)}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Initial state */}
      {!hasSearched && !loading && (
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <p>Search across all your call transcripts</p>
          <p style={{ fontSize: 14, marginTop: 8 }}>
            Find conversations by topic, keyword, or phrase
          </p>
        </div>
      )}
    </div>
  );
}
